import React, { useState, useEffect } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Stack,
  FormHelperText,
  FormControl,
  Grid,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { makeStyles } from "@mui/styles";
import VSNButton from "../../shared/VSNButton";
import VSNLoader from "../../shared/VSNLoader";
import VSNImage from "../../shared/VSNImage";
import waiversFormContainer from "../../container/waiversForm.container";
import VSNBreadcrumbs from "../../shared/VSNBreadcrumbs";
import VSNTypography from "../../shared/VSNTypography";

const useStyles = makeStyles((theme) => ({
  waiverForm: {
    "& .MuiInputBase-input": {
      padding: 10,
    },
    "& .MuiFormControl-root": {
      width: "100%",
    },
  },
  waiveImages: {
    position: "relative",
    padding: "0 30px",
    "&:not(:last-child):after": {
      content: "'X'",
      position: "absolute",
      top: "50%",
      right: 0,
      transform: "translateY(-50%)",
    },
  },
}));

const DynamicForm = () => {
  const {
    waiverFormData,
    waiversFormLoader,
    formData,
    errors,
    handleSubmit,
    handleChange,
    handleCancle,
    isFormChanged,
  } = waiversFormContainer();

  const classes = useStyles();
  const isComplasary = waiverFormData?.id === 1;
  const viewMode = waiverFormData?.signature_status === "Signed";
  const checkBoxEditMode = viewMode && isComplasary;

  useEffect(() => {
    if (waiverFormData?.patient_name) {
      Object?.keys(waiverFormData)?.forEach((key) => {
        handleChange(key, waiverFormData[key]);
      });
    }
  }, [viewMode, waiverFormData]);

  const breadcrumbsRoutes = [
    {
      path: "/edit-profile",
      label: "Account",
    },
    {
      path: `/edit-profile?activeTab=Waivers`,
      label: "Waivers",
    },
    {
      label: waiverFormData?.waiver_name ?? "",
    },
  ];

  if (waiversFormLoader) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <>
      <VSNBreadcrumbs sx={{ p: 2 }} {...{ breadcrumbsRoutes }} />
      <Box
        sx={{
          maxWidth: "80%",
          marginX: "auto",
          marginY: "40px",
          padding: "20px",
        }}
      >
        <Stack direction="row" justifyContent="center" py={4}>
          {waiverFormData?.images &&
            waiverFormData?.images?.map((src, index) => (
              <div className={classes.waiveImages} key={index}>
                <VSNImage
                  key={index}
                  src={src}
                  alt="logo"
                  height="40px"
                  width="180px"
                />
              </div>
            ))}
        </Stack>

        <form className={classes.waiverForm}>
          <Grid container spacing={2}>
            {waiverFormData?.content?.map((field, index) => {
              if (field.type === "text") {
                return (
                  <Grid item xs={12} key={index}>
                    <VSNTypography
                      dangerouslySetInnerHTML={{ __html: field?.title }}
                    />
                  </Grid>
                );
              }

              if (
                ["textbox", "number", "datepicker", "time"].includes(field.type)
              ) {
                return (
                  <Grid item xs={12} sm="auto" key={index}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <VSNTypography
                        sx={{ fontWeight: 600, flex: "none" }}
                        dangerouslySetInnerHTML={{ __html: field?.title }}
                      />

                      {field.type === "textbox" && (
                        <TextField
                          variant="outlined"
                          value={formData[field.key] || ""}
                          onChange={(e) =>
                            handleChange(field.key, e.target.value)
                          }
                          fullWidth
                          error={!!errors[field.key]}
                          helperText={errors[field.key]}
                          required
                          disabled={viewMode}
                        />
                      )}

                      {field.type === "number" && (
                        <TextField
                          type="number"
                          variant="outlined"
                          value={formData[field.key] || ""}
                          onChange={(e) =>
                            handleChange(field.key, e.target.value)
                          }
                          fullWidth
                          error={!!errors[field.key]}
                          helperText={errors[field.key]}
                          required
                          disabled={viewMode}
                        />
                      )}

                      {field.type === "datepicker" && (
                        <Stack>
                          <DesktopDatePicker
                            inputFormat="MM/DD/YYYY"
                            value={
                              formData[field.key]
                                ? dayjs(formData[field.key])
                                : null
                            }
                            onChange={(date) =>
                              handleChange(field.key, dayjs(date))
                            }
                            fullWidth
                            disabled={viewMode}
                            disablePast
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors[field.key]}
                                helperText={errors[field.key]}
                                required
                                fullWidth
                                disabled={viewMode}
                              />
                            )}
                          />

                          {errors && (
                            <VSNTypography
                              sx={{
                                fontSize: "12px",
                                color: "#d32f2f",
                                margin: "3px 14px 0 14px",
                              }}
                            >
                              {errors[field.key]}
                            </VSNTypography>
                          )}
                        </Stack>
                      )}

                      {field.type === "time" && (
                        <Stack>
                          <TimePicker
                            ampm
                            value={
                              formData[field.key]
                                ? dayjs(formData[field.key], "HH:mm")
                                : null
                            }
                            onChange={(time) =>
                              handleChange(
                                field.key,
                                dayjs(time).format("HH:mm")
                              )
                            }
                            fullWidth
                            disabled={viewMode}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors[field.key]}
                                helperText={errors[field.key]}
                                required
                                disabled={viewMode}
                              />
                            )}
                          />
                          {errors && (
                            <VSNTypography
                              sx={{
                                fontSize: "12px",
                                color: "#d32f2f",
                                margin: "3px 14px 0 14px",
                              }}
                            >
                              {errors[field.key]}
                            </VSNTypography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                );
              }

              if (field.type === "checkbox") {
                return (
                  <Grid item xs={12} key={index}>
                    <FormControl error={!!errors[field.key]}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData[field.key] || false}
                            onChange={(e) =>
                              handleChange(field.key, e.target.checked)
                            }
                            disabled={viewMode}
                          />
                        }
                        label={field.title}
                      />
                      {errors[field.key] && (
                        <FormHelperText>{errors[field.key]}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                );
              }

              if (field.type === "optional_checkbox") {
                return (
                  <Grid item xs={12} key={index}>
                    <FormControl error={!!errors[field.key]}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData[field.key] || false}
                            onChange={(e) =>
                              handleChange(field.key, e.target.checked)
                            }
                            // disabled={!checkBoxEditMode}
                          />
                        }
                        label={field.title}
                      />
                      {errors[field.key] && (
                        <FormHelperText>{errors[field.key]}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                );
              }

              return null;
            })}
          </Grid>
        </form>

        <Grid container spacing={2} pt={3}>
          {waiverFormData?.content?.map((field, index) => {
            if (field.type === "reference") {
              return (
                <Grid item xs={12} md={3} key={index}>
                  <VSNTypography
                    dangerouslySetInnerHTML={{ __html: field.title }}
                  />
                </Grid>
              );
            }

            return null;
          })}
        </Grid>

        <Stack direction="row" gap={4} py={4}>
          {(!viewMode || checkBoxEditMode) && (
            <VSNButton handleClick={handleSubmit} disabled={!isFormChanged}>
              Agree and Submit
            </VSNButton>
          )}
          <VSNButton handleClick={handleCancle}>Cancel</VSNButton>
        </Stack>
      </Box>
    </>
  );
};

export default DynamicForm;
